/**
 * The death card. This is the payload.
 *
 * Everything before it — a name plate riding a speech bubble for eight minutes, a
 * voice that reacted to the run you actually had — is setup that makes this land. The
 * bar the whole feature is judged against is one sentence: *a playtester who lost a
 * captain can say that captain's name an hour later, unprompted.* A portrait nobody
 * looks at fails it. This is the one moment the player is not dodging anything, so
 * this is where the face gets spent.
 *
 * **Why 3× and not 1×.** The art pipeline measured it twice, independently: at 1×
 * (64 CSS px) a portrait collapses to two smudges — no mark, no build, no age, no
 * person. `CARD_SCALE` is 3, which is 192 CSS px, which fits a 390 px viewport with
 * room for the plate beside it. That number is the reason there is no portrait in the
 * run HUD.
 *
 * **Order on the card, and why.** Face, then name, then the last thing they said. The
 * face is what you recognise, the name is what you have to be able to *say*, and the
 * line is what makes you want to. The name is the only thing on the card in
 * `--font-pixel` at display size; the line is mono, because it is prose and prose is
 * mono here (AMD-36, two faces, no exceptions).
 *
 * **The line types.** Same 12 ms/char reveal as the run bubble, and it does not
 * auto-hide — `CADENCE.deathHoldMs` is `null`, meaning "hold until the player leaves".
 * They are allowed to sit with it.
 *
 * **Permadeath at the integration layer.** This module composes a portrait, paints it
 * into a canvas and forgets both when the next run starts. It reads no storage, writes
 * no storage, and holds no captain beyond the one on screen. `src/captain/**` is
 * lint-banned from `localStorage`/`sessionStorage` so the fiction cannot be walked
 * back; the same rule is honoured here by owning nothing that outlives the run.
 */

import { CARD_SCALE, composePortrait, paintPortraitToCanvas, recompose, type Portrait } from '../captain/portrait';
import { addressed, type Captain } from '../captain/seed';
import { div, el, span } from './dom';
import { formatTime } from './format';
import { Typewriter } from './captain-speech';

export class CaptainCard {
  readonly root: HTMLElement;

  private readonly canvas: HTMLCanvasElement;
  private readonly given: HTMLElement;
  private readonly epithet: HTMLElement;
  private readonly surname: HTMLElement;
  private readonly meta: HTMLElement;
  private readonly type: Typewriter;
  private readonly lastWrap: HTMLElement;

  /** One buffer for the session; `composePortrait` is the only allocation here. */
  private portrait: Portrait | null = null;
  private shownSeed = -1;

  constructor() {
    this.root = div('captain-card');

    const frame = div('captain-frame');
    this.canvas = el('canvas', 'captain-face');
    // Decorative: the name and the line beside it carry every word of the meaning.
    this.canvas.setAttribute('aria-hidden', 'true');
    frame.appendChild(this.canvas);

    const id = div('captain-id');
    id.appendChild(div('captain-eyebrow', 'CAPTAIN'));
    const nameLine = div('captain-name');
    this.given = span('captain-given', '');
    this.epithet = span('captain-epithet', '');
    this.surname = span('captain-surname', '');
    nameLine.appendChild(this.given);
    nameLine.appendChild(this.epithet);
    nameLine.appendChild(this.surname);
    id.appendChild(nameLine);
    this.meta = div('captain-meta', '');
    id.appendChild(this.meta);

    const last = div('captain-last');
    this.lastWrap = last;
    const body = span('captain-last-body');
    const node = document.createTextNode('');
    body.appendChild(node);
    last.appendChild(body);
    this.type = new Typewriter(node);

    this.root.appendChild(frame);
    this.root.appendChild(id);
    this.root.appendChild(last);
  }

  setReducedMotion(on: boolean): void {
    this.type.instant = on;
  }

  /**
   * Puts a captain on the card. Idempotent for the same run: the defeat screen is
   * presented from two call sites (the UI's own `game_over` handler and the
   * integrator's explicit report), and the line must not retype on the second.
   */
  /**
   * `outcome` exists because this card outlived the screen it was written for. Every
   * string in it assumed a death — "lost at 19:02", a slot held open for a last word —
   * and the victory screen now shows the same face for the opposite reason. A survivor
   * described as lost is worse than no card at all.
   */
  present(captain: Captain, deathLine: string | null, runTimeS: number, outcome: 'lost' | 'survived' = 'lost'): void {
    const seed = captain.runSeed >>> 0;
    if (seed === this.shownSeed) return;
    this.shownSeed = seed;

    // One buffer for the session: the first death allocates, every death after it
    // repaints in place.
    if (this.portrait === null) this.portrait = composePortrait(captain, { scale: CARD_SCALE });
    else recompose(captain, this.portrait);
    paintPortraitToCanvas(this.portrait, this.canvas);
    // The canvas is nearest-neighbour upscaled by CSS, so the backing store size and
    // the CSS size must agree exactly or the browser resamples the pixel grid away.
    this.canvas.style.inlineSize = this.portrait.width + 'px';
    this.canvas.style.blockSize = this.portrait.height + 'px';

    const n = captain.name;
    this.given.textContent = n.given;
    this.epithet.textContent = n.epithet === null ? '' : '“' + n.epithet + '”';
    this.epithet.hidden = n.epithet === null;
    this.surname.textContent = n.surname;
    /*
     * The clock is dropped from this line when the surface showing the card already prints
     * it larger.
     *
     * On the victory panel it did both: `captain-meta` read "Dry · surfaced at 10:28" and
     * `victory-time` repeated "10:28" in 20 px gold twelve pixels below it. The defeat panel
     * has no such headline, so it keeps the time here and reads the same as it always has.
     */
    const fate = outcome === 'survived' ? ' · surfaced' : ' · lost at ';
    const stamp = outcome === 'survived' ? '' : formatTime(runTimeS);
    this.meta.textContent = captain.temperament.label + fate + stamp;
    // The label keeps the time in both cases: a screen reader has no 20 px headline.
    this.root.setAttribute('aria-label', addressed(captain) + ' ' + (outcome === 'survived' ? 'surfaced at' : 'lost at') + ' ' + formatTime(runTimeS));

    // No auto-hide. `CADENCE.deathHoldMs` is null: the line stays until the player
    // leaves the screen. A survivor has no last word, so the quote rule is not drawn at
    // all rather than drawn empty.
    this.type.start(deathLine ?? '');
    this.lastWrap.hidden = deathLine === null;
  }

  /** Snaps the reveal to complete — for a player who taps through. */
  finish(): void {
    if (this.type.isRunning) this.type.finish();
  }

  /** A new run started. The captain on the card is gone and is not coming back. */
  reset(): void {
    this.type.clear();
    this.shownSeed = -1;
  }
}
